import * as React from 'react';
import { Loader2, Save } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useToast } from '../../components/ui/toast';
import {
  useInvoiceSettings,
  useMyMailTemplates,
  useUpdateInvoiceSettings,
  type InvoiceSettingsDTO,
} from '../../hooks/useInvoices';
import {
  usePlatformConfig,
  useUpdatePlatformTaxRates,
  type PlatformConfig,
} from '../../hooks/useBilling';
import { useUser } from '../../auth/AuthProvider';

export function AdminInvoiceSettingsPage() {
  const settings = useInvoiceSettings();
  const update = useUpdateInvoiceSettings();
  const templates = useMyMailTemplates();
  const { user } = useUser();
  const { toast } = useToast();
  const [draft, setDraft] = React.useState<InvoiceSettingsDTO | null>(null);

  React.useEffect(() => {
    if (settings.data?.settings && !draft) {
      const s = settings.data.settings;
      // Default the body-template owner to the current admin if unset.
      setDraft({
        ...s,
        bodyTemplateOwnerUid: s.bodyTemplateOwnerUid || user?.uid || '',
      });
    }
  }, [settings.data, draft, user?.uid]);

  if (settings.isLoading || !draft) {
    return (
      <div className="flex h-32 items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade Rechnungs-Einstellungen …
      </div>
    );
  }

  async function onSave() {
    if (!draft) return;
    try {
      await update.mutateAsync(draft);
      toast({ title: 'Rechnungs-Einstellungen gespeichert' });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button onClick={() => void onSave()} disabled={update.isPending}>
          {update.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Speichern
        </Button>
      </div>

      {/* ── Verkäufer-Daten ───────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Verkäuferdaten (Pflichtangaben für die Rechnung)</CardTitle>
          <CardDescription>
            Werden auf jede ausgestellte Rechnung gedruckt + im X-Rechnung-XML als
            AccountingSupplierParty kodiert.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Firma"
              value={draft.seller.companyName}
              onChange={(v) =>
                setDraft({ ...draft, seller: { ...draft.seller, companyName: v } })
              }
            />
            <Field
              label="Rechnungsverantwortliche*r"
              value={draft.seller.name}
              onChange={(v) => setDraft({ ...draft, seller: { ...draft.seller, name: v } })}
            />
            <Field
              label="UID/USt-ID"
              value={draft.seller.vatId}
              onChange={(v) => setDraft({ ...draft, seller: { ...draft.seller, vatId: v } })}
              placeholder="ATU…"
            />
            <Field
              label="Steuernummer"
              value={draft.seller.taxNumber}
              onChange={(v) =>
                setDraft({ ...draft, seller: { ...draft.seller, taxNumber: v } })
              }
            />
            <Field
              label="E-Mail"
              value={draft.seller.email}
              onChange={(v) => setDraft({ ...draft, seller: { ...draft.seller, email: v } })}
            />
            <Field
              label="Land (ISO-2)"
              value={draft.seller.country}
              maxLength={2}
              onChange={(v) =>
                setDraft({
                  ...draft,
                  seller: { ...draft.seller, country: v.toUpperCase() },
                })
              }
            />
            <Field
              label="Straße + Nr."
              value={draft.seller.line1}
              onChange={(v) => setDraft({ ...draft, seller: { ...draft.seller, line1: v } })}
            />
            <Field
              label="PLZ + Ort"
              value={[draft.seller.postalCode, draft.seller.city].filter(Boolean).join(' ')}
              onChange={(v) => {
                const [pc, ...rest] = v.split(' ');
                setDraft({
                  ...draft,
                  seller: {
                    ...draft.seller,
                    postalCode: pc ?? '',
                    city: rest.join(' '),
                  },
                });
              }}
            />
            <Field
              label="IBAN"
              value={draft.iban}
              onChange={(v) => setDraft({ ...draft, iban: v })}
            />
            <Field
              label="BIC"
              value={draft.bic}
              onChange={(v) => setDraft({ ...draft, bic: v })}
            />
            <Field
              label="Firmenbuch / HR-Nummer"
              value={draft.registerNumber}
              onChange={(v) => setDraft({ ...draft, registerNumber: v })}
              placeholder="z.B. FN 123456a"
            />
            <Field
              label="Firmenbuch-Gericht"
              value={draft.registerCourt}
              onChange={(v) => setDraft({ ...draft, registerCourt: v })}
              placeholder="z.B. Wien"
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Format + Nummerierung ──────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Format &amp; Nummerierung</CardTitle>
          <CardDescription>
            EN 16931-konforme Ausgabe. „PDF + X-Rechnung-XML" ist der sicherste
            Default — der Kunde bekommt beides als Anhang per E-Mail.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Format</Label>
              <Select
                value={draft.format}
                onValueChange={(v) =>
                  setDraft({ ...draft, format: v as InvoiceSettingsDTO['format'] })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf_only">Nur PDF</SelectItem>
                  <SelectItem value="pdf_plus_xrechnung_xml">
                    PDF + X-Rechnung-XML (empfohlen)
                  </SelectItem>
                  <SelectItem value="zugferd_pdf">
                    ZUGFeRD-Hybrid (PDF mit eingebettetem XML)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field
              label="Rechnungsnummern-Präfix"
              value={draft.invoiceNumberPrefix}
              onChange={(v) => setDraft({ ...draft, invoiceNumberPrefix: v })}
              placeholder="z.B. CP3-"
            />
            <NumberField
              label="Zahlungsfrist (Tage)"
              value={draft.paymentDueDays}
              onChange={(v) => setDraft({ ...draft, paymentDueDays: v })}
            />
            <Field
              label="Kleinunternehmer-Vermerk (optional)"
              value={draft.smallBusinessNote}
              onChange={(v) => setDraft({ ...draft, smallBusinessNote: v })}
              placeholder="z.B. §6 Abs.1 Z.27 öUStG"
            />
          </div>
          <div className="mt-3 space-y-1.5">
            <Label>Fußzeile</Label>
            <textarea
              value={draft.footerText}
              onChange={(e) => setDraft({ ...draft, footerText: e.target.value })}
              className="min-h-20 w-full rounded-md border border-border bg-background p-2 text-sm"
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Versand-Konfiguration ────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>E-Mail-Versand</CardTitle>
          <CardDescription>
            Absender + Betreff + Body-Template, das bei jedem Rechnungsversand
            gerendert wird.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex items-center gap-3 rounded-md border border-border bg-muted/30 p-3">
            <Switch
              checked={draft.emailSendingEnabled}
              onCheckedChange={(v) => setDraft({ ...draft, emailSendingEnabled: v })}
            />
            <div className="text-sm">
              <div className="font-medium">E-Mail-Versand aktiviert</div>
              <div className="text-xs text-muted-foreground">
                Wenn aus, werden Rechnungen nur erzeugt + abgelegt, aber nicht verschickt.
              </div>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Absender-Name"
              value={draft.senderName}
              onChange={(v) => setDraft({ ...draft, senderName: v })}
            />
            <Field
              label="Absender-E-Mail"
              value={draft.senderEmail}
              onChange={(v) => setDraft({ ...draft, senderEmail: v })}
              placeholder="z.B. rechnung@deinefirma.at"
            />
            <Field
              label="Reply-To (optional)"
              value={draft.replyToEmail}
              onChange={(v) => setDraft({ ...draft, replyToEmail: v })}
            />
            <Field
              label="Betreff (Merge-Tags möglich)"
              value={draft.subjectTemplate}
              onChange={(v) => setDraft({ ...draft, subjectTemplate: v })}
              placeholder="Ihre Rechnung {{invoiceNumber}} ({{period}})"
            />
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Body-Vorlage (aus dem Mail-Builder)</Label>
              <Select
                value={draft.bodyTemplateId || '__default__'}
                onValueChange={(v) =>
                  setDraft({
                    ...draft,
                    bodyTemplateId: v === '__default__' ? '' : v,
                    bodyTemplateOwnerUid:
                      v === '__default__' ? '' : user?.uid ?? draft.bodyTemplateOwnerUid,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Standard-Body verwenden" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">
                    Standard-Body (eingebaut, kein Builder)
                  </SelectItem>
                  {(templates.data?.items ?? []).map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground">
                Verfügbare Merge-Tags:{' '}
                <code>{'{{invoiceNumber}}'}</code>, <code>{'{{period}}'}</code>,{' '}
                <code>{'{{name}}'}</code>, <code>{'{{grossAmount}}'}</code>,{' '}
                <code>{'{{netAmount}}'}</code>, <code>{'{{dueDate}}'}</code>.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Schedule ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Serien-Versand</CardTitle>
          <CardDescription>
            An einem Tag im Monat zur eingestellten Uhrzeit (lokale Zeit) generiert
            das System für jeden aktiven Anfänger-Modus-Kunden eine Rechnung des
            Vormonats und schickt sie raus.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex items-center gap-3 rounded-md border border-border bg-muted/30 p-3">
            <Switch
              checked={draft.scheduleEnabled}
              onCheckedChange={(v) => setDraft({ ...draft, scheduleEnabled: v })}
            />
            <div className="text-sm">
              <div className="font-medium">Serien-Versand aktiviert</div>
              <div className="text-xs text-muted-foreground">
                Cron läuft alle 30 Minuten und feuert genau einmal am Stichtag.
              </div>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-4">
            <NumberField
              label="Tag im Monat (1–28)"
              value={draft.scheduleDayOfMonth}
              min={1}
              max={28}
              onChange={(v) => setDraft({ ...draft, scheduleDayOfMonth: v })}
            />
            <NumberField
              label="Stunde (0–23)"
              value={draft.scheduleHourLocal}
              min={0}
              max={23}
              onChange={(v) => setDraft({ ...draft, scheduleHourLocal: v })}
            />
            <NumberField
              label="Minute (0–59)"
              value={draft.scheduleMinuteLocal}
              min={0}
              max={59}
              onChange={(v) => setDraft({ ...draft, scheduleMinuteLocal: v })}
            />
            <Field
              label="Zeitzone"
              value={draft.scheduleTimezone}
              onChange={(v) => setDraft({ ...draft, scheduleTimezone: v })}
              placeholder="Europe/Vienna"
            />
          </div>
          {draft.lastFiredKey && (
            <div className="mt-4 text-xs text-muted-foreground">
              Letzter Lauf: <code>{draft.lastFiredKey}</code>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => void onSave()} disabled={update.isPending}>
          {update.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Speichern
        </Button>
      </div>

      <TaxRatesCard />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tax rates — moved here from Platform-Keys; reuses adminUpdatePlatformTaxRates.
// ─────────────────────────────────────────────────────────────────────

function TaxRatesCard() {
  const config = usePlatformConfig();
  const update = useUpdatePlatformTaxRates();
  const { toast } = useToast();
  const [draft, setDraft] = React.useState<PlatformConfig['taxRates'] | null>(null);

  React.useEffect(() => {
    if (config.data?.taxRates && !draft) {
      setDraft({
        ...config.data.taxRates,
        ratesByCountry: { ...config.data.taxRates.ratesByCountry },
      });
    }
  }, [config.data, draft]);

  if (config.isLoading || !draft) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 inline h-3.5 w-3.5 animate-spin" /> Lade
          Steuersätze …
        </CardContent>
      </Card>
    );
  }

  const entries = Object.entries(draft.ratesByCountry);

  function setRate(country: string, value: number) {
    const c = country.toUpperCase();
    setDraft((d) =>
      d ? { ...d, ratesByCountry: { ...d.ratesByCountry, [c]: value } } : d,
    );
  }
  function addRate() {
    setDraft((d) =>
      d ? { ...d, ratesByCountry: { ...d.ratesByCountry, XX: 0 } } : d,
    );
  }
  function removeRate(country: string) {
    setDraft((d) => {
      if (!d) return d;
      const next = { ...d.ratesByCountry };
      delete next[country];
      return { ...d, ratesByCountry: next };
    });
  }

  async function onSave() {
    if (!draft) return;
    try {
      const cleanedRates: Record<string, number> = {};
      for (const [c, v] of Object.entries(draft.ratesByCountry)) {
        if (c.length === 2) cleanedRates[c.toUpperCase()] = v;
      }
      await update.mutateAsync({ ...draft, ratesByCountry: cleanedRates });
      toast({ title: 'Steuersätze gespeichert' });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Steuersätze pro Land</CardTitle>
        <CardDescription>
          MwSt-Rate als Dezimalzahl (0.20 = 20%). „Heimatland" = wo wir Rechnungen
          ausstellen. Kunden mit gültiger UID-Nummer aus einem anderen EU-Land kriegen
          automatisch Reverse-Charge (0%); Kunden außerhalb der EU eine steuerfreie
          Ausfuhrlieferung (0%).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label>Heimatland (ISO-2)</Label>
            <Input
              value={draft.homeCountry}
              maxLength={2}
              onChange={(e) =>
                setDraft({ ...draft, homeCountry: e.target.value.toUpperCase() })
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label>Heimat-MwSt</Label>
            <Input
              type="number"
              step="0.001"
              min={0}
              max={1}
              value={draft.homeRate}
              onChange={(e) =>
                setDraft({ ...draft, homeRate: parseFloat(e.target.value) || 0 })
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label>Default für andere Länder</Label>
            <Input
              type="number"
              step="0.001"
              min={0}
              max={1}
              value={draft.defaultRate}
              onChange={(e) =>
                setDraft({ ...draft, defaultRate: parseFloat(e.target.value) || 0 })
              }
            />
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <Label>Pro-Land-Sätze</Label>
            <Button size="sm" variant="outline" onClick={addRate}>
              + Zeile
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Land (ISO)</TableHead>
                <TableHead>MwSt-Rate</TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map(([country, value]) => (
                <TableRow key={country}>
                  <TableCell className="font-mono">{country}</TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      step="0.001"
                      min={0}
                      max={1}
                      value={value}
                      onChange={(e) =>
                        setRate(country, parseFloat(e.target.value) || 0)
                      }
                      className="h-8 max-w-32"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeRate(country)}
                    >
                      ×
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex justify-end">
          <Button onClick={() => void onSave()} disabled={update.isPending}>
            {update.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Steuersätze speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
      />
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
        className="text-right tabular-nums"
      />
    </div>
  );
}
